import React from 'react'
import { Button, CircularProgress, Typography, Stack, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CanteenCartItem } from '../types'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'
import { Currency, getCurrencySymbol } from '@caterdesk/utils--money'

const canteenCartFooterCurrentMenuDataFragment = graphql(`
  fragment CanteenCartFooter_CurrentMenuDataFragment on CCMealService {
    availableSubsidy
    timeslots {
      id
      startTime
    }
  }
`)

const canteenCartFooterUserDataFragment = graphql(`
  fragment CanteenCartFooter_UserDataFragment on User {
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      topUpEnabled
      userInterfaceSettings {
        hideCCPrices
      }
    }
    wallet {
      balance
    }
  }
`)

type Props = {
  cartItems: CanteenCartItem[]
  cartTotal: number
  onClose: () => void
  onCheckoutClicked: () => void
  loading: boolean
  currency: string
  orderSucceeded: boolean
  menuData: FragmentType<typeof canteenCartFooterCurrentMenuDataFragment>
  userData: FragmentType<typeof canteenCartFooterUserDataFragment>
}

const Footer: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const {
    cartItems,
    cartTotal,
    onClose,
    loading,
    onCheckoutClicked,
    orderSucceeded,
    menuData: menuDataProp,
    userData: userDataProp,
    currency,
  } = props
  const { availableSubsidy } = useFragment(canteenCartFooterCurrentMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenCartFooterUserDataFragment, userDataProp)
  const unlimitedSubsidy = Boolean(userData.subsidySettings.unlimitedSubsidy)
  const totalToPay = unlimitedSubsidy
    ? 0
    : Math.max(cartTotal - availableSubsidy - userData.wallet.balance, 0)
  const insufficientCredits =
    !unlimitedSubsidy && cartTotal > Number(availableSubsidy) + Number(userData.wallet.balance)
  const disableCheckoutButton =
    (insufficientCredits && !userData.account?.topUpEnabled) || cartItems.length === 0

  return (
    <>
      <Divider />
      <Stack direction="column" paddingX={3} paddingY={2}>
        {!userData.account?.userInterfaceSettings.hideCCPrices ? (
          <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">{t('cc:totalToPay')}</Typography>
            <Typography variant="subtitle2">{`${getCurrencySymbol(currency as Currency)}${numberFormat(
              totalToPay,
            )}`}</Typography>
          </Stack>
        ) : null}
      </Stack>
      <Stack
        direction="column"
        paddingX={3}
        paddingY={2}
        bgcolor="background.paper"
        data-cy="checkout-button"
      >
        <Button
          size="large"
          disabled={disableCheckoutButton || loading || orderSucceeded}
          variant="contained"
          fullWidth
          onClick={onCheckoutClicked}
        >
          {loading ? <CircularProgress size={24} /> : t('cc:checkout')}
        </Button>
        <Button size="large" fullWidth variant="text" onClick={onClose}>
          {t('cc:backToMenu')}
        </Button>
      </Stack>
    </>
  )
}

export default Footer
